import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { adminApi } from "@/lib/api";
import { ChartFrame, RankingChart, TrendLineChart, TrendBadge } from "@/components/charts";
import { Download, RefreshCw, EyeOff, Check, X, HelpCircle } from "lucide-react";

/** Auditable "Why is this ranked #1?" — shows the reproducible Community Issue Score. */
const ScoreExplainDialog = ({ range }) => {
  const [data, setData] = useState(null);
  const load = () => adminApi.get("/admin/intelligence/score-explain", { params: { range } })
    .then((r) => setData(r.data)).catch(() => toast.error("Could not load score explanation"));
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="score-explain-btn">
          <HelpCircle className="h-4 w-4 mr-1.5" /> Why is #1 ranked #1?
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Community Issue Score — audit</DialogTitle>
          <DialogDescription>Reproducible arithmetic. AI never decides which issue wins.</DialogDescription>
        </DialogHeader>
        {!data ? <Skeleton className="h-48" /> : (
          <div className="space-y-4" data-testid="score-explain-content">
            <p className="text-xs bg-slate-50 border border-slate-200 rounded p-3 text-foreground leading-relaxed" data-testid="score-formula">
              {data.formula}
            </p>
            <p className="text-xs text-muted-foreground">
              Range: {data.range} · Responses in scope: <span className="font-semibold tabular-nums">{data.total_responses_in_scope}</span>
              {!data.meets_threshold && " (below public threshold — visible to admins only)"}
            </p>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Ranking</p>
              {data.ranking.map((r) => (
                <div key={r.label} className="flex justify-between text-sm border-b border-slate-100 py-1">
                  <span>#{r.rank} {r.label}</span>
                  <span className="tabular-nums text-muted-foreground">score {r.score} ({r.percent}%)</span>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Contributing polls</p>
              {data.contributing_polls.map((p) => (
                <div key={p.poll_id} className="text-xs border border-slate-100 rounded p-2 mb-1.5">
                  <p className="font-medium text-foreground">{p.question}</p>
                  <p className="text-muted-foreground mt-0.5">
                    {p.responses_in_scope} responses in scope · {Object.entries(p.counts).map(([k, v]) => `${k}: ${v}`).join(" · ") || "no selections"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RANGES = [
  { id: "7d", label: "7 Days" }, { id: "30d", label: "30 Days" }, { id: "90d", label: "90 Days" },
  { id: "election", label: "Election Period" }, { id: "all", label: "All Time" },
];

const Stat = ({ label, value, testId }) => (
  <Card><CardContent className="p-4">
    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="font-heading font-bold text-2xl text-foreground tabular-nums" data-testid={testId}>{value}</p>
  </CardContent></Card>
);

export default function AdminIntelligence() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [comments, setComments] = useState([]);
  const [digest, setDigest] = useState(null);

  const load = useCallback(() => {
    setData(null);
    adminApi.get("/admin/intelligence/overview", { params: { range } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load overview"));
    adminApi.get("/admin/intelligence/comments")
      .then((r) => setComments(r.data)).catch(() => {});
    adminApi.get("/admin/digest/subscribers").then((r) => setDigest(r.data)).catch(() => {});
  }, [range]);

  const sendDigestNow = async () => {
    try {
      const r = await adminApi.post("/admin/digest/send-now");
      toast.success(`Weekly Pulse queued for ${r.data.queued} subscriber(s) — mode: ${r.data.email_mode}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Digest send failed"); }
  };

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const r = await adminApi.get("/admin/intelligence/export", { params: { range }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `community-intelligence-${range}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Aggregate CSV exported — contains no respondent identity");
    } catch { toast.error("Export failed"); }
  };

  const moderate = async (id, approved) => {
    try {
      await adminApi.patch(`/admin/intelligence/comments/${id}`, { public_approved: approved });
      toast.success(approved ? "Quote approved for public display" : "Quote approval removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Moderation failed"); }
  };

  const t = data?.totals;

  return (
    <div className="space-y-6" data-testid="admin-intelligence-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Community Intelligence</h1>
          <p className="text-sm text-muted-foreground">Aggregate analytics from real poll responses. Exports never include respondent identity.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="intel-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
          <Button size="sm" onClick={exportCsv} data-testid="intel-export-csv"><Download className="h-4 w-4 mr-1.5" /> Export aggregate CSV</Button>
        </div>
      </div>

      <Tabs value={range} onValueChange={setRange}>
        <TabsList className="h-auto flex-wrap justify-start" data-testid="intel-range-tabs">
          {RANGES.map((r) => <TabsTrigger key={r.id} value={r.id} className="text-xs">{r.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {!data ? <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Responses (range)" value={t.responses} testId="intel-total-responses" />
            <Stat label="Unique participants" value={t.unique_participants} testId="intel-unique-participants" />
            <Stat label="Verified responses" value={t.verified_responses} testId="intel-verified" />
            <Stat label="Comment rate" value={`${t.comment_rate_percent}%`} testId="intel-comment-rate" />
            <Stat label="Responses / published poll" value={t.responses_per_published_poll} testId="intel-completion" />
            <Stat label="Duplicate attempts" value={t.duplicate_attempts} testId="intel-duplicates" />
            <Stat label="Rate-limited" value={t.rate_limited} testId="intel-rate-limited" />
            <Stat label="Test responses (excluded)" value={t.test_responses_excluded} testId="intel-test-excluded" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Card data-testid="intel-top-issues-card"><CardContent className="p-5">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h2 className="font-heading font-bold text-base text-foreground">Top Issues (admin view)</h2>
                <ScoreExplainDialog range={range} />
              </div>
              {data.top_issues.length > 0 ? (
                <>
                  <ChartFrame responses={data.top_issues.reduce((s, i) => Math.max(s, 0), 0) || t.responses} testId="intel-chart">
                    <RankingChart items={data.top_issues} testId="intel-ranking" />
                  </ChartFrame>
                  <div className="mt-2 space-y-1">
                    {data.top_issues.map((i) => (
                      <div key={i.label} className="flex items-center justify-between text-xs gap-2 flex-wrap">
                        <span>{i.label} · {i.count} ({i.percent}%)</span>
                        <TrendBadge trend={i.trend} />
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-sm text-muted-foreground">No issue responses in this range.</p>}
            </CardContent></Card>

            <div className="space-y-5">
              <Card data-testid="intel-series-card"><CardContent className="p-5">
                <h2 className="font-heading font-bold text-base text-foreground mb-2">Responses per week</h2>
                <ChartFrame responses={t.responses_all_time}>
                  <TrendLineChart series={data.series} />
                </ChartFrame>
              </CardContent></Card>

              <Card data-testid="intel-suppressed-card"><CardContent className="p-5">
                <h2 className="font-heading font-bold text-base text-foreground mb-2 inline-flex items-center gap-1.5">
                  <EyeOff className="h-4 w-4 text-muted-foreground" /> Privacy-suppressed areas
                </h2>
                {data.suppressed_areas.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No suppressed areas — all reporting areas meet the threshold ({data.threshold}).</p>
                ) : (
                  <ul className="space-y-1 text-xs text-muted-foreground" data-testid="intel-suppressed-list">
                    {data.suppressed_areas.slice(0, 12).map((s, i) => (
                      <li key={i}>{s.name || s.ward || s.municipality_id} — {s.responses} responses (below public threshold {data.threshold})</li>
                    ))}
                  </ul>
                )}
              </CardContent></Card>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Card data-testid="intel-most-active"><CardContent className="p-5">
              <h2 className="font-heading font-bold text-base text-foreground mb-2">Most active polls</h2>
              <Table><TableHeader><TableRow><TableHead>Poll</TableHead><TableHead className="text-right">Responses</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.most_active_polls.map((p) => (
                    <TableRow key={p.poll_id}><TableCell className="text-xs max-w-[300px] truncate">{p.question}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.responses}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
            </CardContent></Card>
            <Card data-testid="intel-least-active"><CardContent className="p-5">
              <h2 className="font-heading font-bold text-base text-foreground mb-2">Least active polls</h2>
              <Table><TableHeader><TableRow><TableHead>Poll</TableHead><TableHead className="text-right">Responses</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.least_active_polls.map((p) => (
                    <TableRow key={p.poll_id}><TableCell className="text-xs max-w-[300px] truncate">{p.question}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.responses}</TableCell></TableRow>
                  ))}
                </TableBody></Table>
            </CardContent></Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Card data-testid="intel-muni-totals"><CardContent className="p-5">
              <h2 className="font-heading font-bold text-base text-foreground mb-2">Municipality totals</h2>
              {data.municipality_totals.length === 0 ? <p className="text-sm text-muted-foreground">No municipality-tagged responses in this range.</p> : (
                <Table><TableHeader><TableRow><TableHead>Municipality</TableHead><TableHead className="text-right">Responses</TableHead><TableHead>Public</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.municipality_totals.map((m) => (
                      <TableRow key={m.municipality_id}>
                        <TableCell className="text-xs">{m.name || m.municipality_id}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.responses}</TableCell>
                        <TableCell>{m.publicly_visible ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">visible</Badge>
                          : <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[10px]">suppressed</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
              )}
            </CardContent></Card>

            <Card data-testid="intel-ward-totals"><CardContent className="p-5">
              <h2 className="font-heading font-bold text-base text-foreground mb-2">Ward totals</h2>
              {data.ward_totals.length === 0 ? <p className="text-sm text-muted-foreground">No ward-tagged responses in this range.</p> : (
                <Table><TableHeader><TableRow><TableHead>Ward</TableHead><TableHead className="text-right">Responses</TableHead><TableHead>Public</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.ward_totals.map((w, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{w.ward}</TableCell>
                        <TableCell className="text-right tabular-nums">{w.responses}</TableCell>
                        <TableCell>{w.publicly_visible ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">visible</Badge>
                          : <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[10px]">suppressed</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody></Table>
              )}
            </CardContent></Card>
          </div>

          <Card data-testid="intel-digest-card"><CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-base text-foreground">Weekly Community Pulse Digest</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {digest ? `${digest.active} active subscriber(s) (${digest.total} total)` : "Loading subscribers…"} ·
                  Runs Mondays 9:00 AM ET · EMAIL MODE stays SIMULATED — nothing leaves the platform.
                  Personalized by place only, never by answers.
                </p>
              </div>
              <Button size="sm" variant="outline" onClick={sendDigestNow} data-testid="digest-send-now">
                Send weekly digest now (simulated)
              </Button>
            </div>
          </CardContent></Card>

          <Card data-testid="intel-comments-card"><CardContent className="p-5">
            <h2 className="font-heading font-bold text-base text-foreground mb-1">Anonymous comment moderation</h2>
            <p className="text-xs text-muted-foreground mb-3">
              Comments feed automatic theme summaries. A raw comment is only ever quoted publicly after
              explicit approval here — and it is never linked to any person.
            </p>
            {comments.length === 0 ? <p className="text-sm text-muted-foreground">No comments yet.</p> : (
              <Table><TableHeader><TableRow><TableHead>Comment</TableHead><TableHead>Poll</TableHead><TableHead>Public quote</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {comments.slice(0, 30).map((c) => (
                    <TableRow key={c.id} data-testid={`intel-comment-${c.id}`}>
                      <TableCell className="text-xs max-w-[280px]">{c.comment}</TableCell>
                      <TableCell className="text-[11px] max-w-[200px] truncate text-muted-foreground">{c.poll_question}</TableCell>
                      <TableCell>{c.comment_public_approved ? <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">approved</Badge>
                        : <Badge variant="outline" className="bg-slate-50 text-slate-600 text-[10px]">private</Badge>}</TableCell>
                      <TableCell>
                        {c.comment_public_approved ? (
                          <Button size="sm" variant="outline" onClick={() => moderate(c.id, false)} data-testid={`intel-unapprove-${c.id}`}><X className="h-3.5 w-3.5 mr-1" /> Revoke</Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => moderate(c.id, true)} data-testid={`intel-approve-${c.id}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve quote</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
            )}
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
